import React, { useId, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import './FilterableSelect.css';

export interface FilterableSelectOption {
  /** Unique value identifier */
  value: string;
  /** Display text */
  label: string;
  /** Group heading — options with the same group string are grouped together */
  group?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

interface GroupedOptions {
  group: string | null;
  options: FilterableSelectOption[];
}

export interface FilterableSelectProps {
  /** Array of selectable options */
  options: FilterableSelectOption[];
  /** Currently selected value */
  value?: string;
  /** Callback when selection changes — null when cleared */
  onChange?: (value: string | null) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Text shown when no options match the search */
  noResultsText?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Disable the entire component */
  disabled?: boolean;
  /** Validation state */
  state?: 'default' | 'error' | 'success';
  /** Fill the width of the parent container */
  fullWidth?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Name attribute for hidden form input */
  name?: string;
  /** Custom id */
  id?: string;
  /** Accessible label */
  'aria-label'?: string;
  /** ID of element that describes this component */
  'aria-describedby'?: string;
}

export const FilterableSelect: React.FC<FilterableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Type to search...',
  noResultsText = 'No options found',
  size = 'md',
  disabled = false,
  state = 'default',
  fullWidth = false,
  className = '',
  style,
  name,
  id: customId,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const generatedId = useId();
  const componentId = customId || `hi-ui-filterable-select-${generatedId}`;
  const triggerId = `${componentId}-trigger`;
  const listboxId = `${componentId}-listbox`;

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Find the currently selected option
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value]
  );

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Group filtered options — ungrouped first, then by group name
  const groupedFilteredOptions = useMemo<GroupedOptions[]>(() => {
    const groups = new Map<string | null, FilterableSelectOption[]>();

    for (const opt of filteredOptions) {
      const key = opt.group ?? null;
      const existing = groups.get(key);
      if (existing) {
        existing.push(opt);
      } else {
        groups.set(key, [opt]);
      }
    }

    // Ungrouped (null key) first, then named groups in insertion order
    const result: GroupedOptions[] = [];
    const ungrouped = groups.get(null);
    if (ungrouped) {
      result.push({ group: null, options: ungrouped });
    }
    for (const [key, opts] of groups) {
      if (key !== null) {
        result.push({ group: key, options: opts });
      }
    }
    return result;
  }, [filteredOptions]);

  // Flat list of selectable (non-disabled) options for keyboard navigation indexing
  const flatFilteredOptions = useMemo(
    () => filteredOptions.filter((opt) => !opt.disabled),
    [filteredOptions]
  );

  // Open the dropdown
  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
    setHighlightedIndex(-1);
  }, []);

  // Close the dropdown and return focus to trigger
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to wait for the dropdown to render
      const raf = window.requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listboxRef.current) return;

    const option = flatFilteredOptions[highlightedIndex];
    if (!option) return;

    const optionEl = listboxRef.current.querySelector(
      `[data-testid="filterable-select-option-${option.value}"]`
    );
    if (optionEl && typeof optionEl.scrollIntoView === 'function') {
      optionEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, flatFilteredOptions]);

  // Select an option
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      closeDropdown();
    },
    [onChange, closeDropdown]
  );

  // Clear the selection
  const handleClear = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onChange?.(null);
      triggerRef.current?.focus();
    },
    [onChange]
  );

  const handleClearKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClear(e);
      }
    },
    [handleClear]
  );

  // Navigate highlights, skipping disabled options
  const moveHighlight = useCallback(
    (direction: 'next' | 'prev' | 'first' | 'last') => {
      if (flatFilteredOptions.length === 0) return;

      let nextIndex: number;
      switch (direction) {
        case 'first':
          nextIndex = 0;
          break;
        case 'last':
          nextIndex = flatFilteredOptions.length - 1;
          break;
        case 'next':
          nextIndex = highlightedIndex < flatFilteredOptions.length - 1 ? highlightedIndex + 1 : 0;
          break;
        case 'prev':
          nextIndex = highlightedIndex > 0 ? highlightedIndex - 1 : flatFilteredOptions.length - 1;
          break;
      }
      setHighlightedIndex(nextIndex);
    },
    [flatFilteredOptions.length, highlightedIndex]
  );

  // Trigger button click
  const handleTriggerClick = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [isOpen, closeDropdown, openDropdown]);

  // Trigger button keyboard
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) {
            openDropdown();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            openDropdown();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) {
            openDropdown();
          }
          break;
      }
    },
    [isOpen, openDropdown]
  );

  // Search input change
  const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setHighlightedIndex(0);
  }, []);

  // Search input keyboard
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          moveHighlight('next');
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveHighlight('prev');
          break;
        case 'Home':
          e.preventDefault();
          moveHighlight('first');
          break;
        case 'End':
          e.preventDefault();
          moveHighlight('last');
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && flatFilteredOptions[highlightedIndex]) {
            handleSelect(flatFilteredOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
        case 'Tab':
          closeDropdown();
          break;
      }
    },
    [moveHighlight, highlightedIndex, flatFilteredOptions, handleSelect, closeDropdown]
  );

  // Build class names
  const wrapperClasses = [
    'hi-ui-filterable-select',
    `hi-ui-filterable-select--${size}`,
    isOpen && 'hi-ui-filterable-select--open',
    disabled && 'hi-ui-filterable-select--disabled',
    state !== 'default' && `hi-ui-filterable-select--state-${state}`,
    fullWidth && 'hi-ui-filterable-select--full-width',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const triggerTextClasses = [
    'hi-ui-filterable-select__trigger-text',
    !selectedOption && 'hi-ui-filterable-select__trigger-text--placeholder',
  ]
    .filter(Boolean)
    .join(' ');

  // Render an individual option
  const renderOption = (option: FilterableSelectOption) => {
    const flatIndex = flatFilteredOptions.indexOf(option);
    const isHighlighted = flatIndex >= 0 && flatIndex === highlightedIndex;
    const isSelected = option.value === value;

    const optionClasses = [
      'hi-ui-filterable-select__option',
      isHighlighted && 'hi-ui-filterable-select__option--highlighted',
      isSelected && 'hi-ui-filterable-select__option--selected',
      option.disabled && 'hi-ui-filterable-select__option--disabled',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        key={option.value}
        id={`${componentId}-option-${option.value}`}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        className={optionClasses}
        onClick={option.disabled ? undefined : () => handleSelect(option.value)}
        onMouseEnter={option.disabled ? undefined : () => setHighlightedIndex(flatIndex)}
        data-testid={`filterable-select-option-${option.value}`}
      >
        {option.label}
      </div>
    );
  };

  const highlightedOption = highlightedIndex >= 0 ? flatFilteredOptions[highlightedIndex] : null;

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
      style={style}
      data-testid="filterable-select"
    >
      {/* Hidden sizer — forces container width to match longest option label or search placeholder */}
      <div className="hi-ui-filterable-select__sizer" aria-hidden="true">
        <span>{searchPlaceholder}</span>
        {options.map((opt) => (
          <span key={opt.value}>{opt.label}</span>
        ))}
      </div>

      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          data-testid="filterable-select-hidden-input"
        />
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        className="hi-ui-filterable-select__trigger"
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={state === 'error' ? true : undefined}
        data-testid="filterable-select-trigger"
      >
        <span className={triggerTextClasses}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="hi-ui-filterable-select__trigger-icons">
          {value != null && value !== '' && !disabled && (
            <span
              role="button"
              tabIndex={0}
              className="hi-ui-filterable-select__clear-btn"
              onClick={handleClear}
              onKeyDown={handleClearKeyDown}
              aria-label="Clear selection"
              data-testid="filterable-select-clear"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M11 3L3 11M3 3L11 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          )}
          <span className="hi-ui-filterable-select__arrow" aria-hidden="true">
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="hi-ui-filterable-select__dropdown" data-testid="filterable-select-dropdown">
          {/* Search input */}
          <div className="hi-ui-filterable-select__search-container">
            <input
              ref={searchInputRef}
              type="search"
              className="hi-ui-filterable-select__search"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded={true}
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-activedescendant={
                highlightedOption ? `${componentId}-option-${highlightedOption.value}` : undefined
              }
              autoComplete="off"
              data-testid="filterable-select-search"
            />
          </div>

          {/* Listbox */}
          <div
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel || placeholder}
            className="hi-ui-filterable-select__listbox"
            data-testid="filterable-select-listbox"
          >
            {filteredOptions.length === 0 ? (
              <div
                className="hi-ui-filterable-select__no-results"
                data-testid="filterable-select-no-results"
              >
                {noResultsText}
              </div>
            ) : (
              groupedFilteredOptions.map((group) =>
                group.group ? (
                  <div
                    key={group.group}
                    role="group"
                    aria-labelledby={`${componentId}-group-${group.group}`}
                  >
                    <div
                      id={`${componentId}-group-${group.group}`}
                      className="hi-ui-filterable-select__group-header"
                      role="presentation"
                      data-testid={`filterable-select-group-${group.group}`}
                    >
                      {group.group}
                    </div>
                    {group.options.map(renderOption)}
                  </div>
                ) : (
                  <React.Fragment key="__ungrouped">
                    {group.options.map(renderOption)}
                  </React.Fragment>
                )
              )
            )}
          </div>

          {/* Live region for screen reader announcements */}
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="hi-ui-filterable-select__sr-only"
          >
            {filteredOptions.length === 0
              ? 'No results available'
              : `${filteredOptions.length} result${filteredOptions.length !== 1 ? 's' : ''} available`}
          </div>
        </div>
      )}
    </div>
  );
};
